// apps-kb-debug.js - Debug interface for navigation system

import state from './apps-state.js';
import * as navLogic from './apps-kb-navigation-logic.js';
import * as cursor from './apps-kb-cursor.js';
import * as helpers from './apps-kb-helpers.js';
import { keyboardState } from './apps-kb-state.js';

/**
 * Global debug interface for navigation system
 * Usage in console:
 *   window.navDebug.goto(rank, file)
 *   window.navDebug.showInsert(rank, file)
 *   window.navDebug.info(rank, file)
 *   window.navDebug.listPositions(rank)
 */

class NavigationDebugger {
    constructor() {
        console.log('🎯 Navigation Debugger initialized');
        console.log('Available commands:');
        console.log('  navDebug.goto(rank, file) - Move cursor to position');
        console.log('  navDebug.showInsert(rank, file) - Show insert indicator at position');
        console.log('  navDebug.info(rank, file) - Get info about a position');
        console.log('  navDebug.listPositions(rank) - List all valid positions in rank');
        console.log('  navDebug.current() - Show current cursor position');
        console.log('  navDebug.printGrid() - Print visual grid of current screen');
    }

    getContext() {
        const layout = helpers.getLayout();
        const isFolder = !!state.openFolder;
        const currentScreen = helpers.getCurrentScreen();
        const icons = helpers.getCurrentIcons();
        return { layout, isFolder, currentScreen, icons };
    }

    /**
     * Move cursor to specified rank/file position
     */
    goto(rank, file) {
        const { layout, isFolder, currentScreen } = this.getContext();
        
        console.log(`📍 Moving to rank=${rank}, file=${file}`);
        
        // Validate position exists
        const maxRank = navLogic.getMaxRank(layout, isFolder, keyboardState.moveMode.active);
        const maxFile = navLogic.getMaxFile(layout);
        
        if (rank < -1 || rank > maxRank + 1) {
            console.error(`❌ Invalid rank ${rank}. Valid range: -1 to ${maxRank + 1}`);
            return false;
        }
        
        if (file < -1 || file > maxFile + 1) {
            console.error(`❌ Invalid file ${file}. Valid range: -1 to ${maxFile + 1}`);
            return false;
        }
        
        // Activate virtual cursor if not active
        if (!keyboardState.virtualCursor.active) {
            keyboardState.virtualCursor.active = true;
        }
        
        // Update cursor state
        keyboardState.virtualCursor.rank = rank;
        keyboardState.virtualCursor.file = file;
        
        // Handle special positions
        if (rank === -1) {
            keyboardState.virtualCursor.isInModalZone = true;
            keyboardState.virtualCursor.modalZonePosition = 'top';
            cursor.hideVirtualCursor();
            const modal = require('./apps-kb-modal.js');
            modal.highlightModalZone('top');
            console.log('✅ Positioned in TOP MODAL ZONE');
            return true;
        }
        
        if (rank === -0.5 && isFolder) {
            cursor.hideVirtualCursor();
            const folderTitle = document.getElementById('folderTitle');
            if (folderTitle) {
                folderTitle.focus();
                console.log('✅ Positioned on FOLDER TITLE');
                return true;
            }
        }
        
        if (rank > maxRank) {
            keyboardState.virtualCursor.isInModalZone = true;
            keyboardState.virtualCursor.modalZonePosition = 'bottom';
            cursor.hideVirtualCursor();
            const modal = require('./apps-kb-modal.js');
            modal.highlightModalZone('bottom');
            console.log('✅ Positioned in BOTTOM MODAL ZONE');
            return true;
        }
        
        if (file === -1) {
            cursor.hideVirtualCursor();
            const scroll = require('./apps-kb-scroll.js');
            scroll.showScrollZoneHover('left');
            console.log('✅ Positioned in LEFT SCROLL ZONE');
            return true;
        }
        
        if (file >= maxFile + 1) {
            cursor.hideVirtualCursor();
            const scroll = require('./apps-kb-scroll.js');
            scroll.showScrollZoneHover('right');
            console.log('✅ Positioned in RIGHT SCROLL ZONE');
            return true;
        }
        
        // Check if it's an insert position
        if (!Number.isInteger(file)) {
            keyboardState.virtualCursor.isInInsertZone = true;
            if (file === -0.5) {
                keyboardState.virtualCursor.insertZoneType = 'before';
            } else if (file === Math.floor(file) + 0.5) {
                keyboardState.virtualCursor.insertZoneType = file === maxFile + 0.5 ? 'after' : 'between';
            }
        } else {
            keyboardState.virtualCursor.isInInsertZone = false;
            keyboardState.virtualCursor.insertZoneType = null;
        }
        
        // Show cursor at position
        cursor.showVirtualCursor(rank, file);
        console.log(`✅ Cursor positioned at rank=${rank}, file=${file}`);
        return true;
    }

    /**
     * Show insert indicator at specified position (for debugging drop targets)
     */
    showInsert(rank, file) {
        const { layout, isFolder, currentScreen, icons } = this.getContext();
        
        console.log(`📌 Showing insert indicator at rank=${rank}, file=${file}`);
        
        if (!Number.isInteger(rank) || rank < 0) {
            console.error('❌ Insert indicators only valid for integer ranks >= 0');
            return false;
        }
        
        if (Number.isInteger(file) || file < -0.5) {
            console.error('❌ Insert indicators only valid for fractional files >= -0.5');
            return false;
        }
        
        // Get the pixel position for this logical position
        const position = this.getPixelPosition(rank, file);
        if (!position) {
            console.error('❌ Could not calculate pixel position');
            return false;
        }
        
        // Create temporary indicator
        const indicator = document.createElement('div');
        indicator.id = 'debug-insert-indicator';
        indicator.style.cssText = `
            position: fixed;
            left: ${position.x - 3}px;
            top: ${position.y}px;
            width: 6px;
            height: ${position.height}px;
            background: linear-gradient(180deg, #ff00ff 0%, #00ffff 100%);
            border-radius: 3px;
            pointer-events: none;
            z-index: 99999;
            box-shadow: 0 0 10px rgba(255, 0, 255, 0.5);
            animation: debugPulse 1s ease-in-out infinite;
        `;
        
        // Remove old indicator if exists
        const old = document.getElementById('debug-insert-indicator');
        if (old) old.remove();
        
        // Add style for animation
        if (!document.getElementById('debug-insert-style')) {
            const style = document.createElement('style');
            style.id = 'debug-insert-style';
            style.textContent = `
                @keyframes debugPulse {
                    0%, 100% { opacity: 1; transform: scaleX(1); }
                    50% { opacity: 0.6; transform: scaleX(1.5); }
                }
            `;
            document.head.appendChild(style);
        }
        
        document.body.appendChild(indicator);
        console.log(`✅ Insert indicator shown at rank=${rank}, file=${file}`);
        console.log(`   Pixel position: x=${position.x}, y=${position.y}, height=${position.height}`);
        
        // Auto-remove after 5 seconds
        setTimeout(() => {
            if (document.getElementById('debug-insert-indicator')) {
                indicator.remove();
                console.log('ℹ️ Debug indicator auto-removed');
            }
        }, 5000);
        
        return true;
    }

    /**
     * Get pixel position for a logical rank/file coordinate
     */
    getPixelPosition(rank, file) {
        const { layout, isFolder, currentScreen } = this.getContext();
        const container = isFolder ? 
            document.getElementById('folderScreens') : 
            document.getElementById('appspaceScreens');
        
        if (!container) return null;
        
        const maxFile = navLogic.getMaxFile(layout);
        
        // Handle insert positions
        if (!Number.isInteger(file)) {
            if (file === -0.5) {
                // Before first icon
                const firstSlot = this.getSlotElement(rank, 0);
                if (firstSlot) {
                    const rect = firstSlot.getBoundingClientRect();
                    return { x: rect.left - 8, y: rect.top, height: rect.height };
                }
            } else if (file === maxFile + 0.5) {
                // After last icon (append position)
                const lastSlot = this.getSlotElement(rank, maxFile);
                if (lastSlot) {
                    const rect = lastSlot.getBoundingClientRect();
                    return { x: rect.right + 8, y: rect.top, height: rect.height };
                }
            } else if (file > 0 && file < maxFile + 0.5) {
                // Between icons
                const leftFile = Math.floor(file);
                const rightFile = Math.ceil(file);
                const leftSlot = this.getSlotElement(rank, leftFile);
                const rightSlot = this.getSlotElement(rank, rightFile);
                
                if (leftSlot && rightSlot) {
                    const leftRect = leftSlot.getBoundingClientRect();
                    const rightRect = rightSlot.getBoundingClientRect();
                    const x = (leftRect.right + rightRect.left) / 2;
                    return { x, y: leftRect.top, height: leftRect.height };
                }
            }
        } else {
            // Regular icon slot
            const slot = this.getSlotElement(rank, file);
            if (slot) {
                const rect = slot.getBoundingClientRect();
                return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2, height: rect.height };
            }
        }
        
        return null;
    }

    /**
     * Get DOM element for a slot at rank/file
     */
    getSlotElement(rank, file) {
        const { layout, isFolder, currentScreen } = this.getContext();
        
        if (!Number.isInteger(rank) || !Number.isInteger(file)) return null;
        if (rank < 0 || file < 0) return null;
        
        const slotInfo = helpers.rankFileToRegionPosition(rank, file, layout, isFolder);
        if (!slotInfo) return null;
        
        const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
        const screenStartRegion = currentScreen * regionsPerScreen;
        const actualRegion = screenStartRegion + slotInfo.region;
        
        const selector = `.icon-slot[data-region="${actualRegion}"][data-position="${slotInfo.position}"]`;
        return document.querySelector(selector);
    }

    /**
     * Get information about a position
     */
    info(rank, file) {
        const { layout, isFolder, currentScreen, icons } = this.getContext();
        
        console.log('\n📊 Position Information');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log(`Rank: ${rank}, File: ${file}`);
        console.log(`Context: ${isFolder ? 'FOLDER' : 'MAIN SCREEN'}`);
        console.log(`Current Screen: ${currentScreen}`);
        console.log(`Layout: ${layout.leaves} leaves × ${layout.bands} bands`);
        
        // Type detection
        const types = [];
        if (rank === -1) types.push('TOP_ZONE');
        if (rank === -0.5) types.push('FOLDER_TITLE');
        if (rank > navLogic.getMaxRank(layout, isFolder, false)) types.push('BOTTOM_ZONE');
        if (file === -1) types.push('LEFT_SCROLL');
        if (file >= navLogic.getMaxFile(layout) + 1) types.push('RIGHT_SCROLL');
        if (navLogic.isInsertPosition(file)) types.push('INSERT_POSITION');
        if (navLogic.isIconSlot(rank, file, navLogic.getMaxRank(layout, isFolder, false), navLogic.getMaxFile(layout))) {
            types.push('ICON_SLOT');
        }
        
        console.log(`Type: ${types.join(', ') || 'UNKNOWN'}`);
        
        // Occupancy
        if (Number.isInteger(rank) && Number.isInteger(file) && rank >= 0 && file >= 0) {
            const occupied = navLogic.isSlotOccupied(rank, file, icons, layout, isFolder, currentScreen);
            console.log(`Occupied: ${occupied ? 'YES' : 'NO'}`);
            
            if (occupied) {
                const icon = helpers.getIconAtRankFile(rank, file, icons, layout, isFolder);
                if (icon) {
                    console.log(`Icon: "${icon.title}" (id: ${icon.id})`);
                }
            }
        }
        
        // Region/position mapping
        if (Number.isInteger(rank) && Number.isInteger(file) && rank >= 0 && file >= 0) {
            const slotInfo = helpers.rankFileToRegionPosition(rank, file, layout, isFolder);
            if (slotInfo) {
                const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
                const actualRegion = currentScreen * regionsPerScreen + slotInfo.region;
                console.log(`Storage: region=${actualRegion}, position=${slotInfo.position}`);
            }
        }
        
        // Validation for insert positions
        if (navLogic.isInsertPosition(file) && keyboardState.moveMode.active && keyboardState.moveMode.draggedIcon) {
            const valid = navLogic.isValidInsertPosition(
                rank, 
                file, 
                keyboardState.moveMode.draggedIcon, 
                icons, 
                layout, 
                isFolder, 
                currentScreen
            );
            console.log(`Valid Insert Target: ${valid ? 'YES' : 'NO'}`);
        }
        
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
    }

    /**
     * List all valid positions in a rank
     */
    listPositions(rank, includeInserts = false) {
        const { layout, isFolder, currentScreen, icons } = this.getContext();
        const maxFile = navLogic.getMaxFile(layout);
        
        console.log(`\n📋 Positions in Rank ${rank}`);
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        
        const positions = [];
        
        // Scroll zones
        if (navLogic.hasLeftScrollZone(currentScreen)) {
            positions.push({ file: -1, type: 'SCROLL_LEFT' });
        }
        
        // Insert before
        if (includeInserts && navLogic.isSlotOccupied(rank, 0, icons, layout, isFolder, currentScreen)) {
            positions.push({ file: -0.5, type: 'INSERT_BEFORE' });
        }
        
        // Regular positions
        for (let file = 0; file <= maxFile; file++) {
            const occupied = navLogic.isSlotOccupied(rank, file, icons, layout, isFolder, currentScreen);
            const icon = occupied ? helpers.getIconAtRankFile(rank, file, icons, layout, isFolder) : null;
            
            positions.push({ 
                file, 
                type: occupied ? 'ICON' : 'EMPTY',
                icon: icon ? icon.title : null
            });
            
            // Insert between
            if (includeInserts && occupied && file < maxFile) {
                const nextOccupied = navLogic.isSlotOccupied(rank, file + 1, icons, layout, isFolder, currentScreen);
                if (nextOccupied) {
                    positions.push({ file: file + 0.5, type: 'INSERT_BETWEEN' });
                }
            }
        }
        
        // Insert after
        if (includeInserts && navLogic.isSlotOccupied(rank, maxFile, icons, layout, isFolder, currentScreen)) {
            positions.push({ file: maxFile + 0.5, type: 'INSERT_AFTER' });
        }
        
        // Scroll zones
        if (navLogic.hasRightScrollZone(icons, layout, isFolder, currentScreen)) {
            positions.push({ file: maxFile + 1, type: 'SCROLL_RIGHT' });
        }
        
        positions.forEach(pos => {
            const typeLabel = pos.type.padEnd(14);
            const fileLabel = `file=${String(pos.file).padStart(4)}`;
            const iconLabel = pos.icon ? ` "${pos.icon}"` : '';
            console.log(`  ${typeLabel} ${fileLabel}${iconLabel}`);
        });
        
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
    }

    /**
     * Show current cursor position
     */
    current() {
        if (!keyboardState.virtualCursor.active) {
            console.log('❌ Virtual cursor not active');
            return;
        }
        
        const rank = keyboardState.virtualCursor.rank;
        const file = keyboardState.virtualCursor.file;
        
        console.log('\n🎯 Current Cursor Position');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log(`Rank: ${rank}`);
        console.log(`File: ${file}`);
        console.log(`In Modal Zone: ${keyboardState.virtualCursor.isInModalZone}`);
        if (keyboardState.virtualCursor.isInModalZone) {
            console.log(`Modal Zone Position: ${keyboardState.virtualCursor.modalZonePosition}`);
        }
        console.log(`In Insert Zone: ${keyboardState.virtualCursor.isInInsertZone}`);
        if (keyboardState.virtualCursor.isInInsertZone) {
            console.log(`Insert Zone Type: ${keyboardState.virtualCursor.insertZoneType}`);
        }
        console.log(`Move Mode: ${keyboardState.moveMode.active}`);
        if (keyboardState.moveMode.active && keyboardState.moveMode.draggedIcon) {
            console.log(`Dragging: "${keyboardState.moveMode.draggedIcon.title}"`);
        }
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
    }

    /**
     * Print a visual grid of the current screen
     */
    printGrid() {
        const { layout, isFolder, currentScreen, icons } = this.getContext();
        const maxRank = navLogic.getMaxRank(layout, isFolder, false);
        const maxFile = navLogic.getMaxFile(layout);
        
        console.log('\n📐 Current Screen Grid');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log(`Context: ${isFolder ? 'FOLDER' : 'MAIN SCREEN'}`);
        console.log(`Screen: ${currentScreen}`);
        console.log(`Layout: ${layout.leaves} leaves × ${layout.bands} bands`);
        console.log(`Grid: ${maxRank + 1} ranks × ${maxFile + 1} files\n`);
        
        // Header
        let header = '     ';
        for (let file = 0; file <= maxFile; file++) {
            header += ` ${String(file).padStart(2)} `;
        }
        console.log(header);
        console.log('     ' + '━'.repeat((maxFile + 1) * 4));
        
        // Rows
        for (let rank = 0; rank <= maxRank; rank++) {
            let row = `${String(rank).padStart(2)}  │`;
            
            for (let file = 0; file <= maxFile; file++) {
                const occupied = navLogic.isSlotOccupied(rank, file, icons, layout, isFolder, currentScreen);
                const isCursor = keyboardState.virtualCursor.active && 
                               keyboardState.virtualCursor.rank === rank && 
                               keyboardState.virtualCursor.file === file;
                
                if (isCursor) {
                    row += ' 🎯 ';
                } else if (occupied) {
                    row += ' ■  ';
                } else {
                    row += ' ·  ';
                }
            }
            
            console.log(row);
        }
        
        console.log('\n Legend: 🎯 = Cursor  ■ = Icon  · = Empty');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
    }

    /**
     * Convert mouse/touch coordinates to rank/file
     */
    pixelToRankFile(clientX, clientY) {
        const { layout, isFolder, currentScreen } = this.getContext();
        const container = isFolder ? 
            document.getElementById('folderScreens') : 
            document.getElementById('appspaceScreens');
        
        if (!container) {
            console.error('❌ Container not found');
            return null;
        }
        
        // Find the slot at this position
        const element = document.elementFromPoint(clientX, clientY);
        if (!element) return null;
        
        const slot = element.closest('.icon-slot');
        if (!slot) {
            console.log('ℹ️ Not over an icon slot');
            return null;
        }
        
        const region = parseInt(slot.dataset.region);
        const position = parseInt(slot.dataset.position);
        
        // Convert to rank/file
        const visualPos = helpers.regionPositionToRankFile(region, position, layout, isFolder);
        
        console.log(`\n🖱️ Pixel → Rank/File Conversion`);
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log(`Pixel: (${clientX}, ${clientY})`);
        console.log(`Region: ${region}, Position: ${position}`);
        console.log(`Rank: ${visualPos.rank}, File: ${visualPos.file}`);
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
        
        return visualPos;
    }
}

// Create and expose global instance
const navDebugger = new NavigationDebugger();
window.navDebug = navDebugger;

export default navDebugger;
